import { Field, ErrorMessage, FieldArray } from 'formik';
import React, { useEffect } from 'react';

import { getDealCalculation } from '../../../../../redux/actions/transactionApi';
import { useDispatch } from 'react-redux';
import { roundPriceInput } from '../../../../../utils/displayRounding';
import { useDebounce } from '../DebouncedValue';
import { buildDealCalculationPayload } from '../RequestFinancing.helpers';
import {
    isDeliveryChargeOptionDisabled,
    isProductWiseChargeType,
    getChargeProductFieldValue,
    getAdditionalChargeRowKey,
    nextAdditionalChargeRowKey,
    getMatchingProductForCharge,
    getChargeProductOptionValue,
} from './AdditionalCharges.helpers';

const ChargeTypeOptions = ({ charges, deliveryMode }) =>
    charges?.map((chargeOption) => (
        <option
            key={chargeOption.value}
            value={chargeOption.value}
            disabled={isDeliveryChargeOptionDisabled(deliveryMode, chargeOption.value)}>
            {chargeOption.name}
        </option>
    ));

const AdditionalChargeRow = ({ addCharges, index, formikProps, productTypes, platformRole, DropdownConstants, remove, handleAdditionalCharge }) => {
    const deliveryMode = formikProps.values.delivery_mode;
    const chargeTypeOptions =
        formikProps.values?.products.length > 1
            ? DropdownConstants?.DeliveryCharges
            : DropdownConstants?.DeliveryChargesWithOneProduct;
    return (
        <tr className='mt-2' key={getAdditionalChargeRowKey(addCharges)}>
            <td className='col-3'>
                <Field as='select' name={`additional_charges[${index}].charge_type`} className='form-control font-b2'>
                    <option value='' label='Select Option' />
                    <ChargeTypeOptions charges={chargeTypeOptions} deliveryMode={deliveryMode} />
                </Field>
                <ErrorMessage
                    name={`additional_charges[${index}].charge_type`}
                    component='div'
                    className='error-message font-b3 text-error-700'
                />
                {formikProps.values.additional_charges[index].charge_type === 'other' && (
                    <>
                        <Field
                            type='text'
                            name={`additional_charges[${index}].other_charge_name`}
                            className='form-control font-b2 mt-2'
                            placeholder='Enter other charge name here'
                        />
                        <ErrorMessage
                            name={`additional_charges[${index}].other_charge_name`}
                            component='div'
                            className='error-message font-b3 text-error-700'
                        />
                    </>
                )}
            </td>
            <td className='col-3'>
                <Field
                    as='select'
                    name={`additional_charges[${index}].product_ulid`}
                    className='form-control font-b2'
                    disabled={!isProductWiseChargeType(formikProps.values.additional_charges[index].charge_type)}
                    value={getChargeProductFieldValue(formikProps.values.additional_charges[index])}
                    onChange={(e) => handleAdditionalCharge(index, e.target.value)}>
                    <option value='' label='Select Option' />
                    {formikProps.values?.products.map((product) => {
                        const matchingProduct = getMatchingProductForCharge(productTypes, product, platformRole);
                        return (
                            <option
                                key={getChargeProductOptionValue(matchingProduct, platformRole) || product.type || product.name}
                                value={getChargeProductOptionValue(matchingProduct, platformRole)}>
                                {matchingProduct?.name}
                            </option>
                        );
                    })}
                </Field>

                <ErrorMessage
                    name={`additional_charges[${index}].product_ulid`}
                    component='div'
                    className='error-message font-b3 text-error-700'
                />
            </td>
            <td className='col-2'>
                <Field name={`additional_charges[${index}].charge_amount`}>
                    {({ field, form }) => (
                        <input
                            type='number'
                            className='form-control font-b2'
                            disabled={!formikProps.values.additional_charges[index]?.charge_type}
                            value={field.value || ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                form.setFieldValue(field.name, value);
                                field.onChange(e);
                            }}
                            onBlur={(e) => {
                                const value = e.target.value;
                                if (value !== '' && value !== null && value !== undefined && value !== '.') {
                                    const numValue = Number(value);
                                    if (!isNaN(numValue) && numValue >= 0) {
                                        const rounded = roundPriceInput(value);
                                        form.setFieldValue(field.name, rounded);
                                    }
                                }
                                field.onBlur(e);
                            }}
                            step='any'
                            min='0'
                        />
                    )}
                </Field>
                <ErrorMessage
                    name={`additional_charges[${index}].charge_amount`}
                    component='div'
                    className='error-message font-b3 text-error-700'
                />
            </td>
            <td className='col-3'>
                <Field
                    type='text'
                    name={`additional_charges[${index}].remarks`}
                    className='form-control font-b2'
                    disabled={!formikProps.values.additional_charges[index]?.charge_type}
                />
                <ErrorMessage
                    name={`additional_charges[${index}].remarks`}
                    component='div'
                    className='error-message font-b3 text-error-700'
                />
            </td>
            <td className='col-1 text-center'>
                <span className='sf-delete pe-2 font-dh4 cursor ' onClick={() => remove(index)}></span>
            </td>
        </tr>
    );
};

const AdditionalCharges = (props) => {
    const debouncedCharges = useDebounce(props.formikProps.values.additional_charges, 500);
    const debouncedProducts = useDebounce(props.formikProps.values.products, 500);
    const dispatch = useDispatch();

    useEffect(() => {
        const payload = buildDealCalculationPayload({
            platformRole: props.platformRole,
            orgId: props.orgId,
            associatedBuyer: props.associatedBuyer,
            buyerSupplierName: props.formikProps.values.buyer_supplier_name,
            products: debouncedProducts,
            charges: debouncedCharges,
            paymentTerm: props.formikProps.values.payment_term,
            earlyPayment: props.formikProps.values.early_payment_options,
            extendedCredit: props.formikProps.values.extended_credit_option,
            checked: props.formikProps.values.checked,
            transactionType: 'deal_financing',
        });
        dispatch(getDealCalculation(payload));
    }, [debouncedProducts, debouncedCharges]);

    const handleAdditionalCharge = (index, value) => {
        const selectedProduct = props.productTypes.find((p) =>
            props.platformRole !== 'broker' ? p.ulid === value : p.product_ulid === value
        );
        const name = selectedProduct?.name || '';
        props.formikProps.setFieldValue(`additional_charges[${index}].product_ulid`, value);
        props.formikProps.setFieldValue(`additional_charges[${index}].product_name`, name);
    };

    return (
        <>
            <div className='accordion-item'>
                <h2 className='accordion-header' id='flush-headingThree'>
                    <button
                        className='accordion-button collapsed accordion-title px-3'
                        type='button'
                        data-bs-toggle='collapse'
                        data-bs-target='#flush-collapseThree'
                        aria-expanded='false'
                        aria-controls='flush-collapseThree'>
                        Additional Charges
                    </button>
                </h2>
                <div
                    id='flush-collapseThree'
                    className='accordion-collapse collapse show'
                    aria-labelledby='flush-headingThree'
                    data-bs-parent='#accordionFlushExample'>
                    <div className='accordion-body accordion-body p-0 px-1 pb-4'>
                        <div className='col-11 mt-3 px-3'>
                            <table className='table align-top table-hover text-start font-b2'>
                                <thead>
                                    <tr className='bg-neutral-200 font-semiBold'>
                                        <th className='bg-transparent'>Charge Type</th>
                                        <th className='bg-transparent'>Product Name</th>
                                        <th className='bg-transparent'>Amount</th>
                                        <th className='bg-transparent'>Remarks</th>
                                        <th className='bg-transparent'>Action</th>
                                    </tr>
                                </thead>
                                <tbody className='font-b2'>
                                    <FieldArray name='additional_charges'>
                                        {({ push, remove }) => (
                                            <>
                                                {props.formikProps.values?.additional_charges?.map(
                                                    (addCharges, index) => (
                                                        <AdditionalChargeRow
                                                            key={getAdditionalChargeRowKey(addCharges)}
                                                            addCharges={addCharges}
                                                            index={index}
                                                            formikProps={props.formikProps}
                                                            productTypes={props.productTypes}
                                                            platformRole={props.platformRole}
                                                            DropdownConstants={props.DropdownConstants}
                                                            remove={remove}
                                                            handleAdditionalCharge={handleAdditionalCharge}
                                                        />
                                                    )
                                                )}
                                                <td colSpan={4}>
                                                    <div className='row'>
                                                        <div className='col-12 mt-4'>
                                                            <div className='col-12 text-start'>
                                                                <button
                                                                    className='hollow-secondary font-medium secondary-auto'
                                                                    type='button'
                                                                    onClick={() =>
                                                                        push({
                                                                            product_name: '',
                                                                            other_charge_name: '',
                                                                            charge_type: '',
                                                                            product_ulid: '',
                                                                            charge_amount: '',
                                                                            remarks: '',
                                                                            rowKey: nextAdditionalChargeRowKey(),
                                                                        })
                                                                    }>
                                                                    Add Additional Charges
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                    </FieldArray>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdditionalCharges;
